/**
 * The information about an error that a user passes on to the developers: the fields of the error response of the
 * backend (`ErrorDetail` of ChatXDC, see EXCEPTION_HANDLING of mma-backend) and the context of the chat.
 *
 * Every piece of information is a field with a key (`ISSUE_REPORT_FIELDS`). Which fields the report shows, and in
 * which order, is a list of keys: the default is `DEFAULT_ISSUE_REPORT_FIELDS`, and the config (`contact.issueReportFields`)
 * or the prop `reportFields` of `IssueReportDialog` can give another list.
 * 
 * Example:
 * ```
 *  sessionId: chatId,
 *  spaceId: activeSpace?.id,
 *  assistantId: assistant?.config?.endpoint,
 *  reporterName: information?.account?.name,
 *  reporterEmail: information?.account?.username,
 * ```
 */

export interface IssueReportContext {
    sessionId?: string
    spaceId?: string
    assistantId?: string
    reporterName?: string
    reporterEmail?: string
}

export interface IssueReportLine {
    label: string
    value: string
}

type ErrorPayload = Record<string, unknown> | undefined

interface IssueReportFieldDefinition {
    label: string
    /** The value of the field; an empty text when there is none (the field is left out of the report) */
    resolve: (errorPayload: ErrorPayload, context: IssueReportContext) => string
}

const text = (value: unknown): string => (value === undefined || value === null ? '' : String(value).trim())

/** The first value that is there: the backend sends `exception_type`, the types of the frontend say `exceptionType` */
const detail = (details: unknown, ...keys: string[]): string => {
    if (!details || typeof details !== 'object') return ''
    const record = details as Record<string, unknown>
    return keys.map((key) => text(record[key])).find(Boolean) ?? ''
}

/**
 * All the fields that a report can show, in the order a developer needs them (the error first, then where it
 * happened, then who reported it).
 *
 * | Key | Label | Source |
 * | --- | ----- | ------ |
 * | `errorId` | Error ID | `error_id` (YYYYMMDD-HHMMSS-uuid), the key to the log of the backend |
 * | `errorCode` | Error code | `error` (machine readable, e.g. `LINE_DATA_ERROR`) |
 * | `status` | Status | `status` (HTTP status the error stands for) |
 * | `message` | Message | `message` (the message for the user) |
 * | `timestamp` | Time (UTC) | `timestamp` |
 * | `space` | Space | `details.space`, else the active space |
 * | `assistant` | Assistant | `details.assistant`, else the active assistant |
 * | `stage` | Stage | `details.stage` |
 * | `exception` | Exception | `details.exception_type` and `details.exception_message` |
 * | `sessionId` | Session ID | the chat |
 * | `reporter` | Reporter | the signed-in user (name and email) |
 */
export const ISSUE_REPORT_FIELDS = {
    errorId: { label: 'Error ID', resolve: (errorPayload) => text(errorPayload?.error_id) },
    errorCode: { label: 'Error code', resolve: (errorPayload) => text(errorPayload?.error) },
    status: { label: 'Status', resolve: (errorPayload) => text(errorPayload?.status) },
    message: { label: 'Message', resolve: (errorPayload) => text(errorPayload?.message) },
    timestamp: { label: 'Time (UTC)', resolve: (errorPayload) => text(errorPayload?.timestamp) },
    space: {
        label: 'Space',
        resolve: (errorPayload, context) => detail(errorPayload?.details, 'space') || text(context.spaceId),
    },
    assistant: {
        label: 'Assistant',
        resolve: (errorPayload, context) => detail(errorPayload?.details, 'assistant') || text(context.assistantId),
    },
    stage: { label: 'Stage', resolve: (errorPayload) => detail(errorPayload?.details, 'stage') },
    exception: {
        label: 'Exception',
        resolve: (errorPayload) =>
            [
                detail(errorPayload?.details, 'exception_type', 'exceptionType'),
                detail(errorPayload?.details, 'exception_message', 'exceptionMessage'),
            ]
                .filter(Boolean)
                .join(': '),
    },
    sessionId: { label: 'Session ID', resolve: (_errorPayload, context) => text(context.sessionId) },
    reporter: {
        label: 'Reporter',
        resolve: (_errorPayload, context) =>
            [context.reporterName, context.reporterEmail && `<${context.reporterEmail}>`].filter(Boolean).join(' '),
    },
} satisfies Record<string, IssueReportFieldDefinition>

export type IssueReportFieldKey = keyof typeof ISSUE_REPORT_FIELDS

/** The fields that a report shows when nothing else is configured: all of them */
export const DEFAULT_ISSUE_REPORT_FIELDS = Object.keys(ISSUE_REPORT_FIELDS) as IssueReportFieldKey[]

export const isIssueReportFieldKey = (key: unknown): key is IssueReportFieldKey =>
    typeof key === 'string' && Object.prototype.hasOwnProperty.call(ISSUE_REPORT_FIELDS, key)

/**
 * The keys of the fields to show, from a list that comes from outside (the config can hold any text): the keys that do
 * not exist are dropped, and the default list is taken when nothing usable is left.
 */
export const resolveIssueReportFields = (configuredFields: unknown): IssueReportFieldKey[] => {
    if (!Array.isArray(configuredFields)) return DEFAULT_ISSUE_REPORT_FIELDS

    const knownFields = configuredFields.filter(isIssueReportFieldKey)
    return knownFields.length > 0 ? knownFields : DEFAULT_ISSUE_REPORT_FIELDS
}

/**
 * The lines of the report for the given fields, in the order of the given list; a field that has no value is left out.
 */
export const buildIssueReportLines = (
    errorPayload: ErrorPayload,
    context: IssueReportContext = {},
    fields: IssueReportFieldKey[] = DEFAULT_ISSUE_REPORT_FIELDS
): IssueReportLine[] =>
    fields
        .map((key) => ({
            label: ISSUE_REPORT_FIELDS[key].label,
            value: ISSUE_REPORT_FIELDS[key].resolve(errorPayload, context),
        }))
        .filter((line) => line.value)

/** The report as plain text, one `Label: value` per line, to paste into a post of Teams. */
export const formatIssueReport = (lines: IssueReportLine[]): string =>
    lines.map((line) => `${line.label}: ${line.value}`).join('\n')
